import React, { useMemo, useState, useCallback } from 'react';
import { useUser } from '../../contexts/UserContext';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Card,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Badge,
  Icon,
  Flex,
  useColorModeValue,
  Progress,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Divider,
  Tooltip,
} from '@chakra-ui/react';
import {
  FaChartLine,
  FaTrophy,
  FaSearch,
  FaFireAlt,
  FaBullseye,
  FaGlobe,
  FaArrowUp,
  FaArrowDown,
  FaUsers,
  FaDollarSign,
  FaKey,
  FaBalanceScale,
} from 'react-icons/fa';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ScatterChart,
  Scatter,
} from 'recharts';
import { SectionLabel } from './DashboardWrapper';
import BusinessBubbleChart from '../BusinessBubbleChart';
import {
  hasCompetitiveIntel,
  getDomainStrength,
  getMonthlyOrganicValue,
  getMonthlyOrganicClicks,
  getTotalOrganicKeywords,
  getSEOKeywords,
  getTopKeywordsByVolume,
  getKeywordOpportunities,
  getSEOCompetitors,
  getPPCCompetitors,
  getMonthlyPPCBudget,
  getDomainStats,
  formatCurrency,
  formatNumber,
  getDomainStrengthColor,
  getKeywordDifficultyColor,
} from '../../utils/competitiveIntelHelpers';

const COLORS = [
  '#2F80ED',
  '#27AE60',
  '#EB5757',
  '#9B51E0',
  '#F2994A',
  '#219EBC',
  '#F264A6',
  '#BB6BD9',
  '#2D9CDB',
  '#219653',
];

const CompetitiveIntelDashboard = ({ reviewsData = [], demoMode, demoData }) => {
  const [searchQuery, setSearchQuery] = useState('');
  
  // Get favorite URIs from user context
  const { favoriteUris } = useUser();

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const subtitleColor = useColorModeValue('gray.500', 'gray.400');
  const statBg = useColorModeValue('gray.50', 'gray.700');

  // Create a map from shortened business names to their maps_uri for favorites check
  const businessNameToUriMap = useMemo(() => {
    const map = {};
    if (reviewsData) {
      reviewsData.forEach(business => {
        const shortName = business.name?.split(' ').slice(0, 2).join(' ');
        if (shortName && business.maps_uri) {
          map[shortName] = business.maps_uri;
        }
      });
    }
    return map;
  }, [reviewsData]);

  // Custom label renderer for bar charts that shows spinning stars for favorited businesses
  const FavoriteStarBarLabel = useCallback((props) => {
    const { x, y, width, payload } = props;
    const datumName = payload?.name ?? props?.name;
    
    // Check if this business is favorited
    const mapsUri = payload?.maps_uri || payload?.mapsUri || businessNameToUriMap[datumName];
    const isFavorited = mapsUri && favoriteUris?.includes(mapsUri);
    
    if (!isFavorited) return null;
    
    const starSize = 18;
    const starX = x + (width / 2);
    // Keep the star within the visible SVG area (top bars can otherwise clip it)
    const starY = Math.max(y - starSize - 4, 2);
    
    return (
      <g transform={`translate(${starX - starSize/2}, ${starY})`}>
        <g>
          <animateTransform
            attributeName="transform"
            attributeType="XML"
            type="rotate"
            from={`0 ${starSize/2} ${starSize/2}`}
            to={`360 ${starSize/2} ${starSize/2}`}
            dur="3s"
            repeatCount="indefinite"
          />
          <path
            d={`M${starSize/2 + 1},${2} L${starSize*0.63 + 1},${starSize*0.38 + 2} L${starSize + 1},${starSize*0.38 + 2} L${starSize*0.69 + 1},${starSize*0.62 + 2} L${starSize*0.81 + 1},${starSize + 2} L${starSize/2 + 1},${starSize*0.75 + 2} L${starSize*0.19 + 1},${starSize + 2} L${starSize*0.31 + 1},${starSize*0.62 + 2} L${1},${starSize*0.38 + 2} L${starSize*0.37 + 1},${starSize*0.38 + 2} Z`}
            fill="#8B7355"
            fillOpacity="0.3"
          />
          <path
            d={`M${starSize/2},0 L${starSize*0.63},${starSize*0.38} L${starSize},${starSize*0.38} L${starSize*0.69},${starSize*0.62} L${starSize*0.81},${starSize} L${starSize/2},${starSize*0.75} L${starSize*0.19},${starSize} L${starSize*0.31},${starSize*0.62} L0,${starSize*0.38} L${starSize*0.37},${starSize*0.38} Z`}
            fill="#D4AF37"
            stroke="#B8860B"
            strokeWidth="1"
          />
          <path
            d={`M${starSize/2},0 L${starSize*0.63},${starSize*0.38} L${starSize},${starSize*0.38} L${starSize*0.69},${starSize*0.62} L${starSize*0.81},${starSize} L${starSize/2},${starSize*0.75} L${starSize*0.19},${starSize} L${starSize*0.31},${starSize*0.62} L0,${starSize*0.38} L${starSize*0.37},${starSize*0.38} Z`}
            fill="#FFD700"
            fillOpacity="0.7"
          />
          <ellipse
            cx={starSize/2}
            cy={starSize*0.3}
            rx={starSize*0.18}
            ry={starSize*0.14}
            fill="#FFFACD"
            fillOpacity="0.8"
          />
        </g>
      </g>
    );
  }, [businessNameToUriMap, favoriteUris]);

  // Filter businesses with competitive intelligence
  const businessesWithCI = useMemo(() => {
    return reviewsData.filter(business => hasCompetitiveIntel(business));
  }, [reviewsData]);

  // Calculate aggregate statistics
  const stats = useMemo(() => {
    if (!businessesWithCI.length) return null;

    const totalKeywords = businessesWithCI.reduce((sum, b) => sum + getTotalOrganicKeywords(b), 0);
    const totalOrganicValue = businessesWithCI.reduce((sum, b) => sum + getMonthlyOrganicValue(b), 0);
    const totalOrganicClicks = businessesWithCI.reduce((sum, b) => sum + getMonthlyOrganicClicks(b), 0);
    const totalPPCBudget = businessesWithCI.reduce((sum, b) => sum + getMonthlyPPCBudget(b), 0);

    const avgDomainStrength =
      businessesWithCI.reduce((sum, b) => sum + getDomainStrength(b), 0) / businessesWithCI.length;

    return {
      totalBusinesses: businessesWithCI.length,
      totalKeywords,
      totalOrganicValue,
      totalOrganicClicks,
      totalPPCBudget,
      avgDomainStrength,
      avgOrganicValue: totalOrganicValue / businessesWithCI.length,
      avgKeywords: totalKeywords / businessesWithCI.length,
    };
  }, [businessesWithCI]);

  // Top performers by domain strength
  const topPerformers = useMemo(() => {
    return [...businessesWithCI]
      .sort((a, b) => getDomainStrength(b) - getDomainStrength(a))
      .slice(0, 10)
      .map(business => ({
        name: business.name,
        strength: getDomainStrength(business),
        organicValue: getMonthlyOrganicValue(business),
        organicClicks: getMonthlyOrganicClicks(business),
        keywords: getTotalOrganicKeywords(business),
      }));
  }, [businessesWithCI]);

  // Get all keywords across all businesses
  const allKeywords = useMemo(() => {
    const keywords = [];
    businessesWithCI.forEach(business => {
      const businessKeywords = getTopKeywordsByVolume(business, 10);
      businessKeywords.forEach(kw => {
        keywords.push({
          ...kw,
          businessName: business.name,
        });
      });
    });
    return keywords.sort((a, b) => (b.searchVolume || 0) - (a.searchVolume || 0)).slice(0, 20);
  }, [businessesWithCI]);

  // Get all keyword opportunities
  const allOpportunities = useMemo(() => {
    const opportunities = [];
    businessesWithCI.forEach(business => {
      const businessOpps = getKeywordOpportunities(business);
      businessOpps.forEach(opp => {
        opportunities.push({
          ...opp,
          businessName: business.name,
        });
      });
    });
    return opportunities.slice(0, 20);
  }, [businessesWithCI]);

  // Domain strength distribution
  const strengthDistribution = useMemo(() => {
    const ranges = {
      '0-20': 0,
      '21-40': 0,
      '41-60': 0,
      '61-80': 0,
      '81-100': 0,
    };

    businessesWithCI.forEach(business => {
      const strength = getDomainStrength(business);
      if (strength <= 20) ranges['0-20']++;
      else if (strength <= 40) ranges['21-40']++;
      else if (strength <= 60) ranges['41-60']++;
      else if (strength <= 80) ranges['61-80']++;
      else ranges['81-100']++;
    });

    return Object.entries(ranges).map(([range, count], index) => ({
      range,
      count,
      fill: COLORS[index],
    }));
  }, [businessesWithCI]);

  // Organic value chart data
  const organicValueChartData = useMemo(() => {
    return [...businessesWithCI]
      .sort((a, b) => getMonthlyOrganicValue(b) - getMonthlyOrganicValue(a))
      .slice(0, 10)
      .map((business, index) => ({
        name: business.name.split(' ').slice(0, 2).join(' '),
        value: getMonthlyOrganicValue(business),
        fill: COLORS[index % COLORS.length],
      }));
  }, [businessesWithCI]);

  // Filtered businesses for table
  const filteredBusinesses = useMemo(() => {
    if (!searchQuery) return businessesWithCI;
    return businessesWithCI.filter(business =>
      business.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [businessesWithCI, searchQuery]);

  // Businesses with pre-calculated domain strength for bubble chart
  const businessesWithDomainStrength = useMemo(() => {
    return businessesWithCI.map(business => ({
      ...business,
      domainStrength: getDomainStrength(business) || 0
    })).sort((a, b) => b.domainStrength - a.domainStrength);
  }, [businessesWithCI]);

  if (!stats || stats.totalBusinesses === 0) {
    return (
      <Box p={8} textAlign="center">
        <VStack spacing={4}>
          <Icon as={FaBalanceScale} boxSize={16} color="gray.400" />
          <Heading size="lg" color="gray.500">
            No Competitive Intelligence Data
          </Heading>
          <Text color="gray.500" maxW="500px">
            Competitive intelligence data will appear here once businesses have been enriched with
            domain stats, keyword rankings, and competitor analysis.
          </Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box>
      {!demoMode && <SectionLabel description="Real-time competitive data on all businesses in your market. Monitor competitor performance, track market trends, and identify opportunities.">
        Market Intelligence Dashboard
      </SectionLabel>}

      {/* Summary Statistics */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={8}>
        <Card bg={cardBg} shadow="md">
          <CardBody>
            <Stat>
              <StatLabel color={subtitleColor}>
                <HStack>
                  <Icon as={FaUsers} />
                  <Text>Businesses Tracked</Text>
                </HStack>
              </StatLabel>
              <StatNumber color="blue.500">{stats.totalBusinesses}</StatNumber>
              <StatHelpText color={subtitleColor}>
                {((stats.totalBusinesses / reviewsData.length) * 100).toFixed(1)}% of market
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card bg={cardBg} shadow="md">
          <CardBody>
            <Stat>
              <StatLabel color={subtitleColor}>
                <HStack>
                  <Icon as={FaTrophy} />
                  <Text>Avg Domain Strength</Text>
                </HStack>
              </StatLabel>
              <StatNumber color={`${getDomainStrengthColor(stats.avgDomainStrength)}.500`}>
                {stats.avgDomainStrength.toFixed(1)}/100
              </StatNumber>
              <StatHelpText color={subtitleColor}>Market average</StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card bg={cardBg} shadow="md">
          <CardBody>
            <Stat>
              <StatLabel color={subtitleColor}>
                <HStack>
                  <Icon as={FaDollarSign} />
                  <Text>Total Organic Value</Text>
                </HStack>
              </StatLabel>
              <StatNumber color="green.500">{formatCurrency(stats.totalOrganicValue)}</StatNumber>
              <StatHelpText color={subtitleColor}>Monthly across market</StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card bg={cardBg} shadow="md">
          <CardBody>
            <Stat>
              <StatLabel color={subtitleColor}>
                <HStack>
                  <Icon as={FaKey} />
                  <Text>Keywords Tracked</Text>
                </HStack>
              </StatLabel>
              <StatNumber color="purple.500">{formatNumber(stats.totalKeywords)}</StatNumber>
              <StatHelpText color={subtitleColor}>Organic search terms</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Main Content Tabs */}
      <Card bg={cardBg} shadow="md" mb={8}>
        <CardBody>
          <Tabs>
            <TabList>
              <Tab>
                <Icon as={FaTrophy} mr={2} />
                Top Performers
              </Tab>
              <Tab>
                <Icon as={FaSearch} mr={2} />
                Keywords
              </Tab>
              <Tab>
                <Icon as={FaBullseye} mr={2} />
                Opportunities
              </Tab>
              <Tab>
                <Icon as={FaChartLine} mr={2} />
                Analytics
              </Tab>
              <Tab>
                <Icon as={FaBalanceScale} mr={2} />
                All Businesses
              </Tab>
            </TabList>

            <TabPanels>
              {/* Top Performers Tab */}
              <TabPanel>
                <VStack spacing={4} align="stretch">
                  <Heading size="md" mb={2}>
                    Top 10 Domains by Strength
                  </Heading>
                  {topPerformers.map((business, index) => {
                    const strengthColor = getDomainStrengthColor(business.strength);
                    return (
                      <Box
                        key={index}
                        p={4}
                        borderWidth="1px"
                        borderColor={borderColor}
                        borderRadius="lg"
                        bg={statBg}
                      >
                        <Flex justify="space-between" align="center" mb={2}>
                          <HStack spacing={3}>
                            <Badge colorScheme="blue" fontSize="lg">
                              #{index + 1}
                            </Badge>
                            <Text fontWeight="bold" fontSize="lg">
                              {business.name}
                            </Text>
                          </HStack>
                          <Badge colorScheme={strengthColor} variant="solid" fontSize="md" px={3} py={1}>
                            {business.strength}/100
                          </Badge>
                        </Flex>

                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mt={3}>
                          <Box>
                            <Text fontSize="xs" color={subtitleColor}>
                              Organic Value
                            </Text>
                            <Text fontWeight="bold" color="green.600">
                              {formatCurrency(business.organicValue)}/mo
                            </Text>
                          </Box>
                          <Box>
                            <Text fontSize="xs" color={subtitleColor}>
                              Organic Clicks
                            </Text>
                            <Text fontWeight="bold" color="blue.600">
                              {formatNumber(business.organicClicks)}/mo
                            </Text>
                          </Box>
                          <Box>
                            <Text fontSize="xs" color={subtitleColor}>
                              Keywords Ranking
                            </Text>
                            <Text fontWeight="bold" color="purple.600">
                              {formatNumber(business.keywords)}
                            </Text>
                          </Box>
                        </SimpleGrid>
                      </Box>
                    );
                  })}
                </VStack>
              </TabPanel>

              {/* Keywords Tab */}
              <TabPanel>
                <VStack spacing={4} align="stretch">
                  <Heading size="md" mb={2}>
                    Top 20 Keywords by Search Volume
                  </Heading>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    {allKeywords.map((kw, index) => (
                      <Box
                        key={index}
                        p={3}
                        borderWidth="1px"
                        borderColor={borderColor}
                        borderRadius="md"
                      >
                        <Flex justify="space-between" align="center" mb={2}>
                          <Text fontWeight="bold" color={textColor}>
                            {kw.keyword}
                          </Text>
                          <Badge colorScheme="blue" variant="subtle">
                            Rank #{kw.rank}
                          </Badge>
                        </Flex>
                        <Text fontSize="xs" color={subtitleColor} mb={2}>
                          {kw.businessName}
                        </Text>
                        <HStack spacing={2}>
                          <Badge colorScheme="green" fontSize="xs">
                            {formatNumber(kw.searchVolume || 0)} searches/mo
                          </Badge>
                          <Badge
                            colorScheme={getKeywordDifficultyColor(kw.keywordDifficulty || 0)}
                            fontSize="xs"
                          >
                            Difficulty: {kw.keywordDifficulty || 0}
                          </Badge>
                        </HStack>
                      </Box>
                    ))}
                  </SimpleGrid>
                </VStack>
              </TabPanel>

              {/* Opportunities Tab */}
              <TabPanel>
                <VStack spacing={4} align="stretch">
                  <Box>
                    <Heading size="md" mb={2}>
                      Top 20 Keyword Opportunities
                    </Heading>
                    <Text color={subtitleColor} fontSize="sm">
                      High search volume, low difficulty keywords ranked outside top 10
                    </Text>
                  </Box>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    {allOpportunities.map((opp, index) => (
                      <Box
                        key={index}
                        p={3}
                        borderWidth="1px"
                        borderColor="green.200"
                        bg="green.50"
                        borderRadius="md"
                      >
                        <Flex justify="space-between" align="center" mb={2}>
                          <Text fontWeight="bold" color={textColor}>
                            {opp.keyword}
                          </Text>
                          <Badge colorScheme="orange" variant="subtle">
                            Current Rank: #{opp.rank}
                          </Badge>
                        </Flex>
                        <Text fontSize="xs" color={subtitleColor} mb={2}>
                          {opp.businessName}
                        </Text>
                        <HStack spacing={2}>
                          <Badge colorScheme="green" fontSize="xs">
                            {formatNumber(opp.searchVolume || 0)} searches
                          </Badge>
                          <Badge colorScheme="green" fontSize="xs">
                            Difficulty: {opp.keywordDifficulty || 0}
                          </Badge>
                          <Badge colorScheme="blue" fontSize="xs">
                            Score: {((opp.searchVolume || 0) / ((opp.keywordDifficulty || 1) + 1)).toFixed(0)}
                          </Badge>
                        </HStack>
                      </Box>
                    ))}
                  </SimpleGrid>
                </VStack>
              </TabPanel>

              {/* Analytics Tab */}
              <TabPanel>
                <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                  {/* Domain Strength Distribution */}
                  <Box>
                    <Heading size="md" mb={4}>
                      Domain Strength Distribution
                    </Heading>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={strengthDistribution}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="range" />
                        <YAxis domain={[0, (dataMax) => Math.ceil(dataMax + 10)]} />
                        <RechartsTooltip />
                        <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                          {strengthDistribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>

                  {/* Organic Value Distribution */}
                  <Box>
                    <Heading size="md" mb={4}>
                      Top 10 by Organic Value
                    </Heading>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={organicValueChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                        <YAxis domain={[0, (dataMax) => Math.ceil(dataMax + 10)]} />
                        <RechartsTooltip formatter={value => formatCurrency(value)} />
                        <Bar dataKey="value" radius={[8, 8, 0, 0]} label={FavoriteStarBarLabel}>
                          {organicValueChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </SimpleGrid>
              </TabPanel>

              {/* All Businesses Tab */}
              <TabPanel>
                <Box h={{ base: "350px", md: "600px" }}>
                  <BusinessBubbleChart
                    businesses={businessesWithDomainStrength}
                    valueKey="domainStrength"
                    valueLabel="Domain Strength"
                    formatValue={(val) => `${val?.toFixed(0) || '0'}/100`}
                    colorScheme="blue"
                  />
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </CardBody>
      </Card>
    </Box>
  );
};

export default CompetitiveIntelDashboard;
